import mergeToNew from "@insite/client-framework/Common/mergeToNew";
import { makeHandlerChainAwaitable } from "@insite/client-framework/HandlerCreator";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import resetShippingAddressFormValidation from "@insite/client-framework/Store/Pages/CheckoutShipping/Handlers/ResetShippingAddressFormValidation";
import validateShippingAddressForm from "@insite/client-framework/Store/Pages/CheckoutShipping/Handlers/ValidateShippingAddressForm";
import { AnyAction } from "@insite/client-framework/Store/Reducers";
import {
    AddressFieldDisplayCollectionModel,
    BaseAddressModel,
    CountryModel,
    ShipToModel,
} from "@insite/client-framework/Types/ApiModels";
import { CheckoutShippingFormContext } from "@insite/content-library/Pages/CheckoutShippingPage";
import { TextFieldProps } from "@insite/mobius/TextField";
import { formatPhoneNumber } from "@tCooneyBrothers/Common/Utilities/formatPhoneNumber";
import CustomerAddressFormFields, {
    CustomerAddressFormFieldStyles,
} from "@tCooneyBrothers/Overrides/Components/CustomerAddressFormFields";
import React from "react";
import { connect, ResolveThunks } from "react-redux";
import { Dispatch } from "redux";

interface OwnProps {
    countries: CountryModel[];
    fieldDisplay: AddressFieldDisplayCollectionModel;
    extendedStyles?: ShippingAddressFormStyles;
    currentUserIsGuest?: boolean;
}

const mapStateToProps = ({
    pages: {
        checkoutShipping: { shippingAddressFormState },
    },
}: ApplicationState) => ({
    shippingAddressFormState,
});

const mapDispatchToProps = {
    setAddress: (address: ShipToModel) => (dispatch: Dispatch<AnyAction>) => {
        dispatch({
            type: "Pages/CheckoutShipping/SetShippingAddress",
            address,
        });
    },
    validateForm: makeHandlerChainAwaitable<Parameters<typeof validateShippingAddressForm>[0], boolean>(
        validateShippingAddressForm,
    ),
    resetShippingAddressFormValidation,
};

type Props = OwnProps & ReturnType<typeof mapStateToProps> & ResolveThunks<typeof mapDispatchToProps>;

type State = {
    styles: ShippingAddressFormStyles;
};

export interface ShippingAddressFormStyles {
    formFields?: CustomerAddressFormFieldStyles;
}

const baseStyles: ShippingAddressFormStyles = {};

export const oneTimeAddressFormStyles = baseStyles;

class ShippingAddressForm extends React.Component<Props, State> {
    static contextType = CheckoutShippingFormContext;

    declare context: React.ContextType<typeof CheckoutShippingFormContext>;

    constructor(props: Props) {
        super(props);
        const { extendedStyles } = this.props;
        this.state = {
            styles: mergeToNew(baseStyles, extendedStyles),
        };
    }

    componentDidMount() {
        const { validators } = this.context;
        const { shippingAddressFormState, fieldDisplay, validateForm, resetShippingAddressFormValidation } = this.props;
        if (!shippingAddressFormState) {
            return;
        }
        const { address } = shippingAddressFormState;
        const { validation } = address;
        if (!validation) {
            return;
        }

        const shouldValidate =
            validation.firstName?.isDisabled === false ||
            validation.lastName?.isDisabled === false ||
            validation.companyName?.isDisabled === false ||
            validation.attention?.isDisabled === false ||
            validation.address1?.isDisabled === false ||
            validation.address2?.isDisabled === false ||
            validation.address3?.isDisabled === false ||
            validation.address4?.isDisabled === false ||
            validation.city?.isDisabled === false ||
            validation.state?.isDisabled === false ||
            validation.country?.isDisabled === false ||
            validation.postalCode?.isDisabled === false ||
            validation.phone?.isDisabled === false ||
            validation.email?.isDisabled === false ||
            validation.fax?.isDisabled === false;
        if (shouldValidate) {
            validators.shippingAddress = () =>
                validateForm({
                    address,
                    validation,
                    fieldDisplay,
                });
            resetShippingAddressFormValidation();
        }
    }

    componentWillUnmount() {
        const { validators } = this.context;
        validators.shippingAddress = undefined;
    }

    componentDidUpdate(prevProps: Props, prevState: State) {
        const { validators, formSubmitAttempted } = this.context;
        const { shippingAddressFormState: prevShippingAddressFormState, fieldDisplay: prevFieldDisplay } = prevProps;
        const { shippingAddressFormState, fieldDisplay, validateForm } = this.props;
        if (prevShippingAddressFormState !== shippingAddressFormState && shippingAddressFormState) {
            const { address } = shippingAddressFormState;
            const { validation } = address;
            if (
                (!prevShippingAddressFormState ||
                    prevShippingAddressFormState.address !== address ||
                    prevShippingAddressFormState.address.validation !== validation ||
                    prevFieldDisplay !== fieldDisplay) &&
                validation
            ) {
                const shouldValidate =
                    validation.firstName?.isDisabled === false ||
                    validation.lastName?.isDisabled === false ||
                    validation.companyName?.isDisabled === false ||
                    validation.attention?.isDisabled === false ||
                    validation.address1?.isDisabled === false ||
                    validation.address2?.isDisabled === false ||
                    validation.address3?.isDisabled === false ||
                    validation.address4?.isDisabled === false ||
                    validation.city?.isDisabled === false ||
                    validation.state?.isDisabled === false ||
                    validation.country?.isDisabled === false ||
                    validation.postalCode?.isDisabled === false ||
                    validation.phone?.isDisabled === false ||
                    validation.email?.isDisabled === false ||
                    validation.fax?.isDisabled === false;
                if (shouldValidate) {
                    validators.shippingAddress = () =>
                        validateForm({
                            address,
                            validation,
                            fieldDisplay,
                        });

                    if (formSubmitAttempted) {
                        validateForm({
                            address,
                            validation,
                            fieldDisplay,
                        });
                    }
                } else {
                    validators.shippingAddress = undefined;
                }
            }
        }
    }

    handleInputChange: TextFieldProps["onChange"] = event => {
        const { shippingAddressFormState, setAddress } = this.props;
        if (!shippingAddressFormState) {
            return;
        }
        const { address } = shippingAddressFormState;
        const selectedCountry = this.props.countries.find(c => c.id === address.country?.id);
        const isNorthAmericanCountry = selectedCountry?.abbreviation === "US" || selectedCountry?.abbreviation === "CA";
        const propertyValue =
            event.target.name === "phone"
                ? isNorthAmericanCountry
                    ? formatPhoneNumber(event.target.value.replace(/\D/g, ""))
                    : event.target.value.replace(/\D/g, "")
                : event.target.value;
        setAddress({
            ...address,
            [event.target.name]: propertyValue,
        });
    };

    handleInputBlur: TextFieldProps["onBlur"] = event => {
        const { shippingAddressFormState, setAddress } = this.props;
        if (!shippingAddressFormState) {
            return;
        }
        const selectedCountry = this.props.countries.find(c => c.id === address.country?.id);
        const isNorthAmericanCountry = selectedCountry?.abbreviation === "US" || selectedCountry?.abbreviation === "CA";
        const propertyValue =
            event.target.name === "phone"
                ? isNorthAmericanCountry
                    ? formatPhoneNumber(event.target.value.replace(/\D/g, ""))
                    : event.target.value.replace(/\D/g, "")
                : event.target.value;
        const { address } = shippingAddressFormState;
        setAddress({
            ...address,
            [event.target.name]: propertyValue.trim(),
        });
    };

    handleCountryChange = (_: React.ChangeEvent<HTMLSelectElement>, country: BaseAddressModel["country"]) => {
        const { shippingAddressFormState, setAddress } = this.props;
        if (!shippingAddressFormState) {
            return;
        }
        const { address } = shippingAddressFormState;
        !country?.states || country.states.length === 0
            ? setAddress({
                  ...address,
                  country,
                  state: null,
                  phone: "",
              })
            : setAddress({
                  ...address,
                  country,
                  phone: "",
              });
    };

    handleStateChange = (_: React.ChangeEvent<HTMLSelectElement>, state: BaseAddressModel["state"]) => {
        const { shippingAddressFormState, setAddress } = this.props;
        if (!shippingAddressFormState) {
            return;
        }
        const { address } = shippingAddressFormState;
        setAddress({
            ...address,
            state,
        });
    };

    render() {
        const { shippingAddressFormState, countries, fieldDisplay, currentUserIsGuest } = this.props;
        const { styles } = this.state;
        if (!shippingAddressFormState) {
            return null;
        }
        const { address, formErrors } = shippingAddressFormState;
        const {
            firstName,
            lastName,
            attention,
            companyName,
            address1,
            address2,
            address3,
            address4,
            country,
            city,
            state,
            postalCode,
            phone,
            email,
            fax,
            validation,
        } = address;
        if (!validation) {
            return null;
        }

        return (
            <CustomerAddressFormFields
                firstName={firstName}
                lastName={lastName}
                attention={attention}
                companyName={companyName}
                address1={address1}
                address2={address2}
                address3={address3}
                address4={address4}
                country={country}
                city={city}
                state={state}
                postalCode={postalCode}
                phone={phone}
                email={email}
                fax={fax}
                currentUserIsGuest={currentUserIsGuest}
                onChangeFirstName={this.handleInputChange}
                onChangeLastName={this.handleInputChange}
                onChangeAttention={this.handleInputChange}
                onChangeCompanyName={this.handleInputChange}
                onChangeAddress1={this.handleInputChange}
                onChangeAddress2={this.handleInputChange}
                onChangeAddress3={this.handleInputChange}
                onChangeAddress4={this.handleInputChange}
                onBlurFirstName={this.handleInputBlur}
                onBlurLastName={this.handleInputBlur}
                onBlurAttention={this.handleInputBlur}
                onBlurCompanyName={this.handleInputBlur}
                onBlurAddress1={this.handleInputBlur}
                onBlurAddress2={this.handleInputBlur}
                onBlurAddress3={this.handleInputBlur}
                onBlurAddress4={this.handleInputBlur}
                onChangeCountry={this.handleCountryChange}
                onChangeState={this.handleStateChange}
                onChangeCity={this.handleInputChange}
                onChangePostalCode={this.handleInputChange}
                onChangePhone={this.handleInputChange}
                onChangeEmail={this.handleInputChange}
                onChangeFax={this.handleInputChange}
                onBlurCity={this.handleInputBlur}
                onBlurPostalCode={this.handleInputBlur}
                onBlurPhone={this.handleInputBlur}
                onBlurEmail={this.handleInputBlur}
                onBlurFax={this.handleInputBlur}
                firstNameError={formErrors.firstName}
                lastNameError={formErrors.lastName}
                attentionError={formErrors.attention}
                companyNameError={formErrors.companyName}
                address1Error={formErrors.address1}
                address2Error={formErrors.address2}
                address3Error={formErrors.address3}
                address4Error={formErrors.address4}
                countryError={formErrors.country}
                stateError={formErrors.state}
                cityError={formErrors.city}
                postalCodeError={formErrors.postalCode}
                phoneError={formErrors.phone}
                emailError={formErrors.email}
                faxError={formErrors.fax}
                countries={countries}
                validation={validation}
                fieldDisplay={fieldDisplay}
                extendedStyles={styles.formFields}
                isShippingAddress={true}
            />
        );
    }
}

export default connect(mapStateToProps, mapDispatchToProps)(ShippingAddressForm);
